import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { getNotices, createNotice, updateNotice, deleteNotice } from '../../services/api/contentApi';
import { Notice, NoticeFormData } from '../../types/content';
import { formatDateTime } from '../../utils/formatters';

const NoticePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
  }>({
    page: 0,
    limit: 20,
  });
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Notice | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    itemId: string | null;
  }>({
    open: false,
    itemId: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NoticeFormData>({
    defaultValues: {
      title: '',
      content: '',
      isImportant: false,
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['notices', filters],
    queryFn: () => getNotices(filters),
  });

  const createMutation = useMutation({
    mutationFn: createNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      setSnackbar({
        open: true,
        message: '공지사항이 추가되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '공지사항 추가에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NoticeFormData> }) =>
      updateNotice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      setSnackbar({
        open: true,
        message: '공지사항이 수정되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '공지사항 수정에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      setSnackbar({
        open: true,
        message: '공지사항이 삭제되었습니다.',
        severity: 'success',
      });
      setConfirmDialog({ open: false, itemId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '공지사항 삭제에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleOpenAddDialog = () => {
    setEditingItem(null);
    reset({
      title: '',
      content: '',
      isImportant: false,
    });
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (item: Notice) => {
    setEditingItem(item);
    reset({
      title: item.title,
      content: item.content,
      isImportant: item.isImportant,
    });
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingItem(null);
    reset();
  };

  const handleFormSubmit = (data: NoticeFormData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem._id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeleteClick = (id: string) => {
    setConfirmDialog({ open: true, itemId: id });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.itemId) {
      deleteMutation.mutate(confirmDialog.itemId);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ open: false, itemId: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns: Column<Notice>[] = [
    {
      id: 'title',
      label: '제목',
      minWidth: 300,
    },
    {
      id: 'isImportant',
      label: '중요',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'error' : 'default'} label={value ? '중요' : '일반'} />
      ),
    },
    {
      id: 'isActive',
      label: '상태',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '활성' : '비활성'} />
      ),
    },
    {
      id: 'createdAt',
      label: '작성일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
    {
      id: 'actions',
      label: '작업',
      minWidth: 150,
      align: 'center',
      format: (_value: any, row: Notice) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" variant="outlined" onClick={() => handleOpenEditDialog(row)}>
            수정
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDeleteClick(row._id)}
          >
            삭제
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">공지사항 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
          공지사항 추가
        </Button>
      </Box>

      <DataTable
        columns={columns}
        data={(data?.items || []).map(item => ({ ...item, id: item._id }))}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={isLoading}
      />

      {/* 추가/수정 폼 다이얼로그 */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? '공지사항 수정' : '공지사항 추가'}</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <Controller
                name="title"
                control={control}
                rules={{ required: '제목은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="제목"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
              <Controller
                name="content"
                control={control}
                rules={{ required: '내용은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="내용"
                    fullWidth
                    multiline
                    rows={6}
                    error={!!errors.content}
                    helperText={errors.content?.message}
                  />
                )}
              />
              <Controller
                name="isImportant"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="중요 공지사항"
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog}>취소</Button>
            <Button type="submit" variant="contained">
              {editingItem ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="공지사항 삭제"
        message="이 공지사항을 삭제하시겠습니까?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="삭제"
        confirmColor="error"
      />

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NoticePage;
